import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faEye, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { AiOutlineSend } from "react-icons/ai";
import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaTicketAlt,
  FaEnvelope,
  FaPaperclip,
  FaCheckCircle,
  FaClock,
  FaSpinner,
  FaThList,
  FaThLarge,
  FaBuilding,
  FaRegCopy,
  FaCheck,
  FaTimes,
  FaCloud,
  FaExternalLinkAlt,
  FaUsers,
  FaTrash,
  FaFileAlt,
  FaFilePdf,
  FaFileCode,
  FaEye,
  FaLock,
} from 'react-icons/fa';
import { FiRefreshCw, FiAlertTriangle, FiUploadCloud } from 'react-icons/fi';

// Curated list of Cloud Products & Services for Smartnet Magna Global Cloud Solutions
const DEFAULT_CLOUD_PRODUCTS = [
  'Google Cloud Platform (GCP)',
  'Compute Engine (VM)',
  'Google Kubernetes Engine (GKE)',
  'Cloud Run',
  'Cloud Functions',
  'Cloud Storage',
  'Cloud SQL (PostgreSQL / MySQL)',
  'BigQuery & Analytics',
  'Cloud Firestore / Datastore',
  'Cloud Pub/Sub',
  'VPC Network & Cloud Interconnect',
  'Cloud Load Balancing',
  'Cloud IAM & Security',
  'AWS Cloud Services',
  'Microsoft Azure Cloud Services',
  'Google Cloud Managed Services',
];

const PRIORITY_CONFIG = {
  'P1 - Critical': {
    label: 'P1 - Critical',
    color: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800',
    sla: 'SLA < 1 Jam',
  },
  'P2 - High impact': {
    label: 'P2 - High Impact',
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800',
    sla: 'SLA < 4 Jam',
  },
  'P3 - Medium impact': {
    label: 'P3 - Medium Impact',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-800',
    sla: 'SLA < 8 Jam',
  },
  'P4 - Low impact': {
    label: 'P4 - Low Impact',
    color: 'bg-gray-50 text-gray-700 border-gray-200',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-800',
    sla: 'SLA < 24 Jam',
  },
};

function SupportRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketError, setTicketError] = useState(null);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, Open, On Progress, Closed
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '', status: null });

  // Comments state for ticket details
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [isDraggingCommentFiles, setIsDraggingCommentFiles] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const commentFileInputRef = useRef(null);
  const chatBottomRef = useRef(null);

  // Companies & Services options
  const [companies, setCompanies] = useState([]);
  const [cloudProducts, setCloudProducts] = useState(DEFAULT_CLOUD_PRODUCTS);

  // Clipboard copy state
  const [copiedId, setCopiedId] = useState(null);

  // User state
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user')) || {};
    } catch {
      return {};
    }
  }, []);
  const role = user ? user.role : null;
  const isAdmin = role === 'Admin';
  const userId = user ? user.id_user : null;
  const userCompanyId = user ? user.company_id : null;
  const userEmail = user ? user.email || '' : '';

  // -------------------------
  // CREATE TICKET FORM STATE (Single 1-Page Form)
  // -------------------------
  const [createForm, setCreateForm] = useState({
    subject: '',
    product: DEFAULT_CLOUD_PRODUCTS[0],
    priority: 'P3 - Medium impact',
    contact: userEmail,
    description: '',
    errorMessage: '',
    attachment: null,
    attachments: [],
  });
  const [ccList, setCcList] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [ccError, setCcError] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [showCcDropdown, setShowCcDropdown] = useState(false);
  const ccDropdownRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdTicketResult, setCreatedTicketResult] = useState(null);
  const [isDraggingTicketFiles, setIsDraggingTicketFiles] = useState(false);
  const autoOpenedTicketRef = useRef(false);

  // -------------------------
  // EDIT TICKET FORM STATE
  // -------------------------
  const [editForm, setEditForm] = useState({
    product_list: '',
    describe_issue: '',
    detail_issue: '',
    priority: 'P3 - Medium impact',
    contact: '',
    status: 'Open',
    cc_emails: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Toggle Sidebar
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Fetch Tickets
  const fetchTickets = useCallback(async () => {
    setLoadingTickets(true);
    setTicketError(null);
    try {
      const url =
        role === 'Admin'
          ? `${process.env.REACT_APP_API_BASE_URL}/api/tickets/`
          : `${process.env.REACT_APP_API_BASE_URL}/api/tickets/user/${userId}`;

      const res = await axios.get(url);
      setTickets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setTicketError('Failed to load tickets. Please try again.');
    } finally {
      setLoadingTickets(false);
    }
  }, [role, userId]);

  // Fetch Companies (for Admin filter)
  const fetchCompanies = useCallback(async () => {
    if (role !== 'Admin') return;
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/customers`);
      if (Array.isArray(res.data)) {
        setCompanies(
          res.data.map((c) => ({
            value: c.company_id,
            label: c.company_name || c.company_id,
          }))
        );
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  }, [role]);

  // Fetch Cloud Products/Services
  const fetchCloudServices = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/services/`);
      const rawList = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      if (rawList.length > 0) {
        const names = rawList.map((item) => item.service_name || item.name).filter(Boolean);
        // Merge with DEFAULT_CLOUD_PRODUCTS ensuring unique values
        const merged = Array.from(new Set([...names, ...DEFAULT_CLOUD_PRODUCTS]));
        setCloudProducts(merged);
      }
    } catch (err) {
      console.warn('Using default Cloud products list:', err);
    }
  }, []);

  // Fetch Company Users (for CC autocomplete suggestions)
  const fetchCompanyUsers = useCallback(async () => {
    try {
      const targetCompanyId = userCompanyId;
      if (targetCompanyId) {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/users/company/${targetCompanyId}`
          );
          if (Array.isArray(res.data) && res.data.length > 0) {
            setCompanyUsers(res.data);
            return;
          }
        } catch (companyErr) {
          console.warn('Company users endpoint returned error, trying fallback:', companyErr);
        }
      }
      // Fallback: fetch all users and filter by company if available
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users`);
      if (Array.isArray(res.data)) {
        if (targetCompanyId) {
          const filtered = res.data.filter(
            (u) =>
              u.company_id === targetCompanyId ||
              (user?.company_name && u.company_name === user.company_name)
          );
          setCompanyUsers(filtered.length > 0 ? filtered : res.data);
        } else {
          setCompanyUsers(res.data);
        }
      }
    } catch (err) {
      console.warn('Could not fetch company users for CC:', err);
    }
  }, [userCompanyId, user?.company_name]);

  useEffect(() => {
    if (userId) {
      fetchTickets();
      fetchCompanies();
      fetchCloudServices();
      fetchCompanyUsers();
    }
  }, [userId, fetchTickets, fetchCompanies, fetchCloudServices, fetchCompanyUsers]);

  // Click outside listener to dismiss CC dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ccDropdownRef.current && !ccDropdownRef.current.contains(e.target)) {
        setShowCcDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch Comments for active ticket
  const fetchComments = async (tId) => {
    if (!tId) return;
    setLoadingComments(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/tickets/comment/${tId}`);
      const sorted = (res.data || []).sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      setComments(sorted);
      setTimeout(() => {
        chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const processCommentFiles = (incomingFiles) => {
    const files = Array.from(incomingFiles || []);
    if (!files.length) return;

    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB per file
    const oversized = files.filter((f) => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      setErrorModal({
        isOpen: true,
        title: 'File Size Exceeded (Max 5 MB)',
        message: `The following file(s) exceed the 5 MB limit:\n${oversized
          .map((f) => `• ${f.name} (${formatFileSize(f.size)})`)
          .join('\n')}\n\nPlease choose files under 5 MB.`,
        status: 413,
      });
      return;
    }

    setCommentFiles((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const newFiles = [...prev];
      for (const file of files) {
        if (!existingKeys.has(`${file.name}-${file.size}`)) {
          newFiles.push(file);
        }
      }
      return newFiles;
    });
  };

  const handleCommentFileChange = (e) => {
    processCommentFiles(e.target.files);
    if (e.target) e.target.value = '';
  };

  const handleCommentDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCommentFiles(true);
  };

  const handleCommentDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    if (!isDraggingCommentFiles) setIsDraggingCommentFiles(true);
  };

  const handleCommentDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingCommentFiles(false);
  };

  const handleCommentDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCommentFiles(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processCommentFiles(e.dataTransfer.files);
      if (e.dataTransfer.clearData) e.dataTransfer.clearData();
    }
  };

  const handleCommentPaste = (e) => {
    if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
      const files = Array.from(e.clipboardData.files);
      if (files.length > 0) {
        processCommentFiles(files);
      }
    }
  };

  const handleRemoveCommentFile = (indexToRemove) => {
    setCommentFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSendComment = async () => {
    if (!activeTicket || (!commentText.trim() && commentFiles.length === 0)) return;

    const MAX_SIZE = 5 * 1024 * 1024;
    const oversized = commentFiles.filter((f) => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      setErrorModal({
        isOpen: true,
        title: 'File Size Exceeded (Max 5 MB)',
        message: `The following file(s) exceed the 5 MB limit:\n${oversized
          .map((f) => `• ${f.name} (${formatFileSize(f.size)})`)
          .join('\n')}\n\nPlease choose files under 5 MB.`,
        status: 413,
      });
      return;
    }

    setIsSubmittingComment(true);
    try {
      const formData = new FormData();
      formData.append('id_user', userId || 'user');
      formData.append('comment', commentText.trim());
      commentFiles.forEach((file) => {
        formData.append('attachment', file);
      });

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/comment/${activeTicket.ticket_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setCommentText('');
      setCommentFiles([]);
      if (commentFileInputRef.current) {
        commentFileInputRef.current.value = '';
      }
      fetchComments(activeTicket.ticket_id);
    } catch (err) {
      console.error('Error posting comment:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg = 'Failed to send comment. Please try again.';
      if (status === 413) {
        msg = 'The attached files are too large. Maximum size is 5 MB per file.';
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      setErrorModal({
        isOpen: true,
        title: 'Failed to Send Comment',
        message: msg,
        status,
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // -------------------------
  // CC EMAIL CHIP MANAGEMENT
  // -------------------------
  const validateEmail = (email) => {
    return String(email)
      .trim()
      .toLowerCase()
      .match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const addCcEmail = (emailToAdd) => {
    const trimmed = (emailToAdd || '').trim();
    if (!trimmed) return;

    // Check multiple emails if separated by comma, semicolon, or whitespace
    const splitEmails = trimmed
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    const invalidEmails = [];
    const duplicateEmails = [];
    const newEmails = [];
    const activeContact = (userEmail || createForm.contact || '').trim().toLowerCase();

    splitEmails.forEach((email) => {
      const cleanEmail = email.toLowerCase();
      if (!validateEmail(cleanEmail)) {
        invalidEmails.push(email);
      } else if (activeContact && cleanEmail === activeContact) {
        duplicateEmails.push(`${email} (Primary contact)`);
      } else if (ccList.some((e) => e.toLowerCase() === cleanEmail)) {
        duplicateEmails.push(email);
      } else {
        newEmails.push(cleanEmail);
      }
    });

    if (invalidEmails.length > 0) {
      setCcError(`Invalid email format: ${invalidEmails.join(', ')}. Please use name@domain.com`);
      return;
    }

    if (duplicateEmails.length > 0 && newEmails.length === 0) {
      setCcError(`Email already added: ${duplicateEmails.join(', ')}`);
      return;
    }

    setCcError('');
    if (newEmails.length > 0) {
      setCcList((prev) => [...prev, ...newEmails]);
    }
    setCcInput('');
    setShowCcDropdown(false);
  };

  const removeCcEmail = (emailToRemove) => {
    setCcList((prev) => prev.filter((e) => e.toLowerCase() !== emailToRemove.toLowerCase()));
  };

  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (ccInput.trim()) {
        addCcEmail(ccInput);
      }
    } else if (e.key === 'Escape') {
      setShowCcDropdown(false);
    }
  };

  // Filter company users for CC suggestions based on current query and exclusions
  const filteredCompanyUsers = useMemo(() => {
    const query = ccInput.trim().toLowerCase();
    const activeContact = (userEmail || createForm.contact || '').trim().toLowerCase();
    return companyUsers.filter((u) => {
      const email = (u.email || '').trim();
      if (!email) return false;
      // Do not suggest the requester's own email (which is already the Contact Email)
      if (email.toLowerCase() === activeContact) return false;
      // Do not suggest emails already added to CC
      if (ccList.some((e) => e.toLowerCase() === email.toLowerCase())) return false;
      // If query is empty, show company colleagues
      if (!query) return true;
      const fullName = (u.full_name || '').toLowerCase();
      const username = (u.username || '').toLowerCase();
      return (
        email.toLowerCase().includes(query) ||
        fullName.includes(query) ||
        username.includes(query)
      );
    });
  }, [companyUsers, ccInput, ccList, userEmail, createForm.contact]);

  // Copy to clipboard helper
  const handleCopyId = (ticketId, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(ticketId);
    setCopiedId(ticketId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Attachment helpers
  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const parseAttachmentUrls = (attachmentStr) => {
    if (!attachmentStr) return [];
    const s = String(attachmentStr).trim();
    if (!s || s.toLowerCase() === 'none' || s.toLowerCase() === 'null') return [];
    if (s.includes('|||')) {
      return s.split('|||').map((u) => u.trim()).filter(Boolean);
    }
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map((u) => String(u).trim()).filter(Boolean);
      } catch (e) {}
    }
    // Split on comma only if followed by http:// or https:// (avoids breaking URLs with comma in company path)
    return s.split(/,(?=\s*https?:\/\/)/).map((u) => u.trim()).filter(Boolean);
  };

  const processTicketFiles = (incomingFiles) => {
    const files = Array.from(incomingFiles || []);
    if (!files.length) return;

    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB per file
    const oversized = files.filter((f) => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      setErrorModal({
        isOpen: true,
        title: 'File Size Exceeded (Max 5 MB)',
        message: `Some selected files exceed the 5 MB limit: ${oversized
          .map((f) => `${f.name} (${formatFileSize(f.size)})`)
          .join(', ')}. Please choose files smaller than 5 MB.`,
      });
    }

    const validFiles = files.filter((f) => f.size <= MAX_SIZE);
    if (!validFiles.length) return;

    setCreateForm((prev) => {
      const existingKeys = new Set((prev.attachments || []).map((f) => `${f.name}-${f.size}`));
      const newFiles = [...(prev.attachments || [])];
      for (const file of validFiles) {
        if (!existingKeys.has(`${file.name}-${file.size}`)) {
          newFiles.push(file);
        }
      }
      return {
        ...prev,
        attachments: newFiles,
        attachment: newFiles[0] || null,
      };
    });
  };

  const handleFileChange = (e) => {
    processTicketFiles(e.target.files);
    if (e.target) e.target.value = '';
  };

  const handleTicketDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingTicketFiles(true);
  };

  const handleTicketDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    if (!isDraggingTicketFiles) {
      setIsDraggingTicketFiles(true);
    }
  };

  const handleTicketDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingTicketFiles(false);
  };

  const handleTicketDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingTicketFiles(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processTicketFiles(e.dataTransfer.files);
      if (e.dataTransfer.clearData) e.dataTransfer.clearData();
    }
  };

  const handleTicketPaste = (e) => {
    if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
      const files = Array.from(e.clipboardData.files);
      if (files.length > 0) {
        processTicketFiles(files);
      }
    }
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setCreateForm((prev) => {
      const updated = (prev.attachments || []).filter((_, idx) => idx !== indexToRemove);
      return {
        ...prev,
        attachments: updated,
        attachment: updated[0] || null,
      };
    });
  };

  // -------------------------
  // CREATE TICKET (Single Page Form Submission)
  // -------------------------
  const handleOpenCreateModal = () => {
    setCreateForm({
      subject: '',
      product: cloudProducts[0] || 'Google Cloud Platform (GCP)',
      priority: 'P3 - Medium impact',
      contact: userEmail || '',
      description: '',
      errorMessage: '',
      attachment: null,
      attachments: [],
    });
    setCcList([]);
    setCcInput('');
    setCcError('');
    setShowCcDropdown(false);
    setIsDraggingTicketFiles(false);
    fetchCompanyUsers();
    setShowCreateModal(true);
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();

    if (!createForm.subject.trim()) {
      alert('Please enter a ticket subject.');
      return;
    }
    if (!createForm.description.trim()) {
      alert('Please enter a description.');
      return;
    }
    if (!createForm.contact.trim() || !validateEmail(createForm.contact.trim())) {
      alert('Please enter a valid contact email.');
      return;
    }

    // If there is lingering input in CC, add it
    if (ccInput.trim()) {
      if (validateEmail(ccInput.trim())) {
        if (!ccList.includes(ccInput.trim())) {
          ccList.push(ccInput.trim());
        }
      }
    }

    setIsSubmitting(true);
    const combinedCc = ccList.join(', ');

    const payload = {
      company_id: userCompanyId,
      id_user: userId,
      product_list: createForm.product,
      describe_issue: createForm.subject.trim(),
      detail_issue: createForm.errorMessage.trim()
        ? `${createForm.description.trim()}\n\n[Observed Error / Logs]:\n${createForm.errorMessage.trim()}`
        : createForm.description.trim(),
      contact: createForm.contact.trim(),
      priority: createForm.priority,
      domain: 'cloud', // Domain locked to cloud as requested
      cc_emails: combinedCc,
    };

    const formData = new FormData();
    formData.append('ticket', JSON.stringify(payload));
    formData.append('company_id', payload.company_id || '');
    formData.append('id_user', payload.id_user || '');
    formData.append('product_list', payload.product_list);
    formData.append('describe_issue', payload.describe_issue);
    formData.append('detail_issue', payload.detail_issue);
    formData.append('contact', payload.contact);
    formData.append('priority', payload.priority);
    formData.append('domain', 'cloud');
    formData.append('cc_emails', combinedCc);

    if (createForm.attachments && createForm.attachments.length > 0) {
      createForm.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
      formData.append('attachment', createForm.attachments[0]);
    } else if (createForm.attachment) {
      formData.append('attachment', createForm.attachment);
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setShowCreateModal(false);
      setCreatedTicketResult(res.data);
      setShowSuccessModal(true);
      fetchTickets(); // Refresh list
    } catch (err) {
      console.error('Error creating ticket:', err);
      let errMsg = 'Failed to create ticket. Please check your connection.';
      let errTitle = 'Submission Failed';
      if (err.response?.status === 413) {
        errTitle = 'File Size Too Large (HTTP 413)';
        errMsg = 'The uploaded attachment files exceed the server limit. Please ensure each file is under 5 MB and try again.';
      } else if (err.response?.data?.detail) {
        errMsg =
          typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : JSON.stringify(err.response.data.detail);
      } else if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      }
      setErrorModal({
        isOpen: true,
        title: errTitle,
        message: errMsg,
        status: err.response?.status,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------
  // VIEW DETAILS (NAVIGATE TO DEDICATED PAGE)
  // -------------------------
  const handleViewTicket = (ticket) => {
    if (!ticket?.ticket_id) return;
    navigate(`/support-request/${ticket.ticket_id}`);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
  };

  // Auto-redirect to dedicated ticket page if URL query parameter (?ticket_id=... or ?id=...) is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetTicketId = (params.get('ticket_id') || params.get('id') || '').trim();
    if (targetTicketId) {
      navigate(`/support-request/${targetTicketId}`, { replace: true });
    }
  }, [location.search, navigate]);

  // -------------------------
  // EDIT TICKET
  // -------------------------
  const handleOpenEdit = (ticket, e) => {
    if (e) e.stopPropagation();
    if (!isAdmin) return;
    setActiveTicket(ticket);
    setEditForm({
      product_list: ticket.product_list || cloudProducts[0],
      describe_issue: ticket.describe_issue || '',
      detail_issue: ticket.detail_issue || '',
      priority: ticket.priority || 'P3 - Medium impact',
      contact: ticket.contact || '',
      status: ticket.status || 'Open',
      cc_emails: ticket.cc_emails || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateTicket = async (e) => {
    e.preventDefault();
    if (!isAdmin || !activeTicket) return;
    setIsUpdating(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/${activeTicket.ticket_id}`,
        editForm
      );
      setShowEditModal(false);
      fetchTickets();
      if (showDetailModal && activeTicket) {
        setActiveTicket((prev) => ({ ...prev, ...editForm }));
      }
    } catch (err) {
      console.error('Failed to update ticket:', err);
      alert('Failed to update ticket.');
    } finally {
      setIsUpdating(false);
    }
  };

  // -------------------------
  // DELETE TICKET
  // -------------------------
  const handleOpenDelete = (ticket, e) => {
    if (e) e.stopPropagation();
    if (!isAdmin) return;
    setActiveTicket(ticket);
    setShowDeleteModal(true);
  };

  const handleDeleteTicket = async () => {
    if (!isAdmin || !activeTicket) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/${activeTicket.ticket_id}`
      );
      setShowDeleteModal(false);
      if (showDetailModal) setShowDetailModal(false);
      fetchTickets();
    } catch (err) {
      console.error('Failed to delete ticket:', err);
      alert('Failed to delete ticket.');
    }
  };

  // -------------------------
  // FILTERED TICKETS COMPUTATION
  // -------------------------
  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      // Company filter
      if (selectedCompany && t.company_id !== selectedCompany) {
        return false;
      }
      // Status filter
      if (statusFilter !== 'ALL' && t.status !== statusFilter) {
        return false;
      }
      // Priority filter
      if (priorityFilter !== 'ALL' && t.priority !== priorityFilter) {
        return false;
      }
      // Search text filter
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const idMatch = (t.ticket_id || '').toLowerCase().includes(q);
        const subjectMatch = (t.describe_issue || '').toLowerCase().includes(q);
        const productMatch = (t.product_list || '').toLowerCase().includes(q);
        const contactMatch = (t.contact || '').toLowerCase().includes(q);
        const ccMatch = (t.cc_emails || '').toLowerCase().includes(q);
        if (!idMatch && !subjectMatch && !productMatch && !contactMatch && !ccMatch) {
          return false;
        }
      }
      return true;
    });
  }, [tickets, selectedCompany, statusFilter, priorityFilter, searchTerm]);

  // Statistics
  const stats = useMemo(() => {
    const total = tickets.length;
    const open = tickets.filter((t) => t.status === 'Open').length;
    const inProgress = tickets.filter((t) => t.status === 'On Progress').length;
    const closed = tickets.filter((t) => t.status === 'Closed').length;
    return { total, open, inProgress, closed };
  }, [tickets]);

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      {/* Header */}
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

      <div className="flex flex-1 min-w-0 max-w-full overflow-x-hidden pt-16">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        {/* Main Content Area */}
        <main
          className={`flex-1 min-w-0 max-w-full overflow-x-hidden transition-all duration-300 ease-in-out px-3 sm:px-6 py-6 sm:py-8 ${
            sidebarOpen ? 'lg:ml-64' : 'ml-0'
          } ml-0`}
        >
          <div className="max-w-7xl mx-auto space-y-6">

            {/* ---------------------------------------------------- */}
            {/* HERO / TOP BANNER: Title & Quick Create Ticket Action */}
            {/* ---------------------------------------------------- */}
            <div className="bg-gradient-to-r from-[#11498B] via-[#165ab0] to-[#1e3a8a] rounded-2xl p-6 md:p-8 text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
              <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none"></div>
              <div className="absolute left-1/2 top-0 w-32 h-32 bg-blue-400/10 rounded-full blur-xl pointer-events-none"></div>

              <div className="space-y-2 z-10">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-xs font-medium text-blue-100">
                  <FaCloud className="text-sky-300" />
                  <span>Cloud Support</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                  Support Ticket Portal
                </h1>
                <p className="text-blue-100/90 text-sm md:text-base max-w-2xl leading-relaxed">
                  Submit and track your support tickets. Add CC recipients to keep your team in the loop.
                </p>
              </div>

              <div className="flex items-center gap-3 z-10 w-full md:w-auto">
                <button
                  onClick={fetchTickets}
                  disabled={loadingTickets}
                  className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium text-sm transition-all flex items-center justify-center gap-2 shadow-sm"
                  title="Refresh tickets"
                >
                  <FiRefreshCw className={`${loadingTickets ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>

                <button
                  onClick={handleOpenCreateModal}
                  className="flex-1 md:flex-initial px-6 py-3 rounded-xl bg-white text-[#11498B] font-bold text-sm hover:bg-blue-50 transition-all transform hover:-translate-y-0.5 active:translate-y-0 shadow-lg flex items-center justify-center gap-2"
                >
                  <FaPlus className="text-xs" />
                  <span>New Ticket</span>
                </button>
              </div>
            </div>

            {/* ---------------------------------------------------- */}
            {/* KPI STAT CARDS: Interactive Quick Status Filters */}
            {/* ---------------------------------------------------- */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Card 1: Total */}
              <div
                onClick={() => setStatusFilter('ALL')}
                className={`p-5 rounded-2xl border transition-all cursor-pointer bg-white ${
                  statusFilter === 'ALL'
                    ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md'
                    : 'border-slate-200/80 hover:border-slate-300 shadow-sm hover:shadow'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total Tickets
                  </span>
                  <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center">
                    <FaTicketAlt className="text-sm" />
                  </div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-2xl md:text-3xl font-extrabold text-slate-800">
                    {stats.total}
                  </span>
                  <span className="text-xs text-slate-400">all time</span>
                </div>
              </div>

              {/* Card 2: Open */}
              <div
                onClick={() => setStatusFilter(statusFilter === 'Open' ? 'ALL' : 'Open')}
                className={`p-5 rounded-2xl border transition-all cursor-pointer bg-white ${
                  statusFilter === 'Open'
                    ? 'border-blue-600 ring-2 ring-blue-600/20 shadow-md'
                    : 'border-slate-200/80 hover:border-slate-300 shadow-sm hover:shadow'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                    Open
                  </span>
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <FaClock className="text-sm" />
                  </div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-2xl md:text-3xl font-extrabold text-blue-700">
                    {stats.open}
                  </span>
                  <span className="text-xs text-blue-500 font-medium">awaiting response</span>
                </div>
              </div>

              {/* Card 3: On Progress */}
              <div
                onClick={() =>
                  setStatusFilter(statusFilter === 'On Progress' ? 'ALL' : 'On Progress')
                }
                className={`p-5 rounded-2xl border transition-all cursor-pointer bg-white ${
                  statusFilter === 'On Progress'
                    ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-md'
                    : 'border-slate-200/80 hover:border-slate-300 shadow-sm hover:shadow'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
                    On Progress
                  </span>
                  <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                    <FaSpinner className="text-sm animate-spin" />
                  </div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-2xl md:text-3xl font-extrabold text-amber-700">
                    {stats.inProgress}
                  </span>
                  <span className="text-xs text-amber-500 font-medium">in progress</span>
                </div>
              </div>

              {/* Card 4: Closed */}
              <div
                onClick={() => setStatusFilter(statusFilter === 'Closed' ? 'ALL' : 'Closed')}
                className={`p-5 rounded-2xl border transition-all cursor-pointer bg-white ${
                  statusFilter === 'Closed'
                    ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-md'
                    : 'border-slate-200/80 hover:border-slate-300 shadow-sm hover:shadow'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                    Closed
                  </span>
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <FaCheckCircle className="text-sm" />
                  </div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-2xl md:text-3xl font-extrabold text-emerald-700">
                    {stats.closed}
                  </span>
                  <span className="text-xs text-emerald-500 font-medium">resolved</span>
                </div>
              </div>
            </div>

            {/* ---------------------------------------------------- */}
            {/* TOOLBAR: Search, Filters, Company & View Mode */}
            {/* ---------------------------------------------------- */}
            <div className="bg-white p-4 md:p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search ticket ID, subject, service, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* Filters Group */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Priority Filter */}
                <div className="relative">
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none pr-8 cursor-pointer"
                  >
                    <option value="ALL">All Priorities</option>
                    <option value="P1 - Critical">P1 - Critical</option>
                    <option value="P2 - High impact">P2 - High Impact</option>
                    <option value="P3 - Medium impact">P3 - Medium Impact</option>
                    <option value="P4 - Low impact">P4 - Low Impact</option>
                  </select>
                  <FaFilter className="w-3 h-3 absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>

                {/* Company Filter (Admin Only) */}
                {role === 'Admin' && companies.length > 0 && (
                  <div className="relative">
                    <select
                      value={selectedCompany}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                      className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none pr-8 cursor-pointer max-w-[180px] truncate"
                    >
                      <option value="">All Companies</option>
                      {companies.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <FaBuilding className="w-3 h-3 absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                )}

                {/* Reset Filters button if filters are active */}
                {(statusFilter !== 'ALL' || priorityFilter !== 'ALL' || selectedCompany || searchTerm) && (
                  <button
                    onClick={() => {
                      setStatusFilter('ALL');
                      setPriorityFilter('ALL');
                      setSelectedCompany('');
                      setSearchTerm('');
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 rounded hover:bg-blue-50 transition"
                  >
                    Reset Filter
                  </button>
                )}

                {/* View Switcher: Table vs Cards */}
                <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200/80">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`p-1.5 rounded-lg text-sm transition-all ${
                      viewMode === 'table'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Table View"
                  >
                    <FaThList />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-lg text-sm transition-all ${
                      viewMode === 'grid'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title="Card View"
                  >
                    <FaThLarge />
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message banner */}
            {ticketError && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <FiAlertTriangle className="text-red-500 flex-shrink-0" />
                  <span>{ticketError}</span>
                </div>
                <button
                  onClick={fetchTickets}
                  className="font-semibold underline hover:no-underline text-xs"
                >
                  Retry
                </button>
              </div>
            )}

            {/* ---------------------------------------------------- */}
            {/* TICKET LISTING: Standard Helpdesk Table & Grid Views */}
            {/* ---------------------------------------------------- */}
            {loadingTickets ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 mb-4 animate-pulse">
                  <FaSpinner className="text-2xl animate-spin" />
                </div>
                <h3 className="text-base font-bold text-slate-800">Loading Tickets...</h3>
                <p className="text-slate-400 text-sm mt-1">
                  Fetching your support tickets.
                </p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 text-slate-400 mb-4">
                  <FaTicketAlt className="text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-slate-800">No tickets found</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto mt-1 mb-6">
                  {searchTerm || statusFilter !== 'ALL' || priorityFilter !== 'ALL'
                    ? 'No tickets match your current search or filter criteria.'
                    : 'No tickets submitted yet. Create a new ticket to get started.'}
                </p>
                <button
                  onClick={handleOpenCreateModal}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition shadow-sm"
                >
                  <FaPlus className="text-xs" />
                  <span>New Ticket</span>
                </button>
              </div>
            ) : viewMode === 'table' ? (
              /* ==================================================== */
              /* STANDARD HELPDESK DATA TABLE VIEW                   */
              /* ==================================================== */
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <th className="py-3.5 px-4 min-w-[260px]">Issue</th>
                        <th className="py-3.5 px-4 min-w-[160px]">Services</th>
                        <th className="py-3.5 px-4">Priority</th>
                        <th className="py-3.5 px-4">Status</th>
                        <th className="py-3.5 px-4 min-w-[180px]">Contact</th>
                        <th className="py-3.5 px-4 min-w-[150px]">Created</th>
                        <th className="py-3.5 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {filteredTickets.map((ticket) => {
                        const prio = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG['P3 - Medium impact'];

                        return (
                          <tr
                            key={ticket.ticket_id}
                            onClick={() => handleViewTicket(ticket)}
                            className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                          >
                            {/* Subject & Preview */}
                            <td className="py-4 px-4">
                              <div className="font-semibold text-slate-800 line-clamp-1 group-hover:text-blue-600 transition">
                                {ticket.describe_issue || 'Untitled'}
                              </div>
                              <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                                {ticket.detail_issue || ''}
                              </div>
                            </td>

                            {/* Cloud Product */}
                            <td className="py-4 px-4 whitespace-nowrap">
                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-sky-800 border border-sky-200/60 text-xs font-medium">
                                <FaCloud className="text-sky-500 text-[10px]" />
                                <span className="truncate max-w-[140px]">{ticket.product_list}</span>
                              </div>
                            </td>

                            {/* Priority */}
                            <td className="py-4 px-4 whitespace-nowrap">
                              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${prio.color}`}>
                                <span className={`w-2 h-2 rounded-full ${prio.dot}`}></span>
                                <span>{ticket.priority?.replace(' - ', ' ') || 'P3 Medium'}</span>
                              </div>
                            </td>

                            {/* Status */}
                            <td className="py-4 px-4 whitespace-nowrap">
                              {ticket.status === 'Open' ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                  Open
                                </span>
                              ) : ticket.status === 'On Progress' ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                  On Progress
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  Closed
                                </span>
                              )}
                            </td>

                            {/* Contact */}
                            <td className="py-4 px-4 whitespace-nowrap">
                              <div className="text-xs text-slate-700 flex items-center gap-1">
                                <FaEnvelope className="text-slate-400 text-[11px]" />
                                <span className="truncate max-w-[150px]">{ticket.contact || '-'}</span>
                              </div>
                            </td>

                            {/* Created Date */}
                            <td className="py-4 px-4 whitespace-nowrap text-xs text-slate-500">
                              <div className="flex items-center gap-1.5">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-slate-400 text-xs" />
                                <span>
                                  {ticket.created_at
                                    ? new Date(ticket.created_at).toLocaleDateString('en-US', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '-'}
                                </span>
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="py-4 px-4 whitespace-nowrap text-right">
                              <div
                                className="flex items-center justify-end gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={() => handleViewTicket(ticket)}
                                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                  title="View Ticket Details"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                                {isAdmin && (
                                  <>
                                    <button
                                      onClick={(e) => handleOpenEdit(ticket, e)}
                                      className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                                      title="Edit Ticket"
                                    >
                                      <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button
                                      onClick={(e) => handleOpenDelete(ticket, e)}
                                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                      title="Delete Ticket"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer */}
                <div className="py-3 px-4 bg-slate-50 border-t border-slate-200/80 text-xs text-slate-500 flex items-center justify-between">
                  <span>
                    Showing <strong>{filteredTickets.length}</strong> of{' '}
                    <strong>{tickets.length}</strong> tickets
                  </span>
                  <span className="text-slate-400">Click any row to view details</span>
                </div>
              </div>
            ) : (
              /* ==================================================== */
              /* RESPONSIVE CARD / GRID VIEW                         */
              /* ==================================================== */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredTickets.map((ticket) => {
                  const prio = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG['P3 - Medium impact'];

                  return (
                    <div
                      key={ticket.ticket_id}
                      onClick={() => handleViewTicket(ticket)}
                      className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer p-5 flex flex-col justify-between group"
                    >
                      {/* Top Bar */}
                      <div>
                        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                          <span className="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200/60">
                            {ticket.ticket_id}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {ticket.status === 'Open' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                Open
                              </span>
                            ) : ticket.status === 'On Progress' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                On Progress
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Closed
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Subject & Preview */}
                        <div className="mt-3">
                          <h3 className="font-bold text-slate-800 text-base group-hover:text-blue-600 transition line-clamp-1">
                            {ticket.describe_issue || 'Untitled'}
                          </h3>
                          <p className="text-xs text-slate-500 line-clamp-2 mt-1">
                            {ticket.detail_issue || 'No additional details.'}
                          </p>
                        </div>

                        {/* Cloud Product & Priority */}
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-50 text-sky-800 border border-sky-200/60 text-xs font-medium">
                            <FaCloud className="text-sky-500 text-[10px]" />
                            {ticket.product_list}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${prio.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`}></span>
                            {ticket.priority?.replace(' - ', ' ')}
                          </span>
                        </div>
                      </div>

                      {/* Footer Info */}
                      <div className="mt-5 pt-3 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="truncate max-w-[150px] font-medium text-slate-700">
                            {ticket.contact}
                          </span>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleViewTicket(ticket)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 transition"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={(e) => handleOpenEdit(ticket, e)}
                                className="p-1.5 text-slate-400 hover:text-amber-600 transition"
                                title="Edit"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                onClick={(e) => handleOpenDelete(ticket, e)}
                                className="p-1.5 text-slate-400 hover:text-red-600 transition"
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ========================================================================= */}
      {/* 1-PAGE CREATE TICKET MODAL: Comprehensive, Fast, No multi-step wizard!    */}
      {/* ========================================================================= */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden border border-slate-200/80 animate-in fade-in zoom-in-95 duration-200 my-8">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#11498B] to-[#1D4ED8] p-6 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                  <FaTicketAlt className="text-lg text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">New Support Ticket</h2>
                  <p className="text-blue-100 text-xs">
                    Fill in the details below to submit a new ticket.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body Form */}
            <form
              onSubmit={handleSubmitTicket}
              onPaste={handleTicketPaste}
              className="p-6 md:p-8 space-y-6 max-h-[80vh] overflow-y-auto"
            >

              {/* SECTION 1: Subject, Cloud Product, Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Subject */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. GKE Node Pool fails to scale under high traffic"
                    value={createForm.subject}
                    onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition placeholder:text-slate-400"
                  />
                </div>

                {/* Cloud Product (Focused on Cloud as requested) */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <FaCloud className="text-sky-500" />
                    <span>Cloud Product / Service</span> <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={createForm.product}
                    onChange={(e) => setCreateForm({ ...createForm, product: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
                  >
                    {cloudProducts.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>

                </div>

                {/* Priority */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={createForm.priority}
                    onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition font-medium"
                  >
                    <option value="P1 - Critical">P1 - Critical</option>
                    <option value="P2 - High impact">P2 - High Impact</option>
                    <option value="P3 - Medium impact">P3 - Medium Impact</option>
                    <option value="P4 - Low impact">P4 - Low Impact</option>
                  </select>
                </div>
              </div>

              {/* SECTION 2: Contact Email & CC to other users */}
              <div className="bg-slate-50/70 p-5 rounded-2xl border border-slate-200/80 space-y-4">
                <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
                  <FaEnvelope className="text-blue-600" />
                  <span>Contact & Email Notifications</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Primary Contact Email (Locked to requesting user) */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-between">
                      <span>
                        Contact Email <span className="text-red-500">*</span>
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 flex items-center gap-1">
                        <FaLock className="text-[9px]" />
                        <span>Account Email (Read-only)</span>
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        readOnly
                        required
                        placeholder="email@company.com"
                        value={userEmail || createForm.contact}
                        className="w-full px-3 py-2 pr-9 rounded-xl border border-slate-200 bg-slate-100 text-slate-600 text-sm cursor-not-allowed select-none font-medium focus:outline-none"
                        title="Contact email is locked to your logged-in account email"
                      />
                      <FaLock className="absolute right-3 top-2.5 text-slate-400 text-xs pointer-events-none" />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Tickets are submitted on behalf of your logged-in account email.
                    </p>
                  </div>

                  {/* CC Emails to other users with Company autocomplete */}
                  <div className="relative" ref={ccDropdownRef}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <FaUsers className="text-purple-600" />
                        <span>CC (Optional)</span>
                      </span>
                      <span className="text-[11px] text-slate-400 font-normal">Select colleague or enter any manual email</span>
                    </label>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Type name or email address..."
                        value={ccInput}
                        onFocus={() => setShowCcDropdown(true)}
                        onChange={(e) => {
                          setCcInput(e.target.value);
                          setShowCcDropdown(true);
                          if (ccError) setCcError('');
                        }}
                        onKeyDown={handleCcKeyDown}
                        className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (ccInput.trim()) {
                            addCcEmail(ccInput);
                          }
                        }}
                        className="px-3.5 py-2 rounded-xl bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold text-xs transition flex items-center gap-1 cursor-pointer"
                      >
                        + Add
                      </button>
                    </div>

                    {/* CC Dropdown Menu (Company Members + Custom / Manual Email Option) */}
                    {showCcDropdown && (
                      <div className="absolute z-30 left-0 right-0 mt-1.5 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden max-h-64 overflow-y-auto">
                        {/* Custom / Manual Email Option (shown whenever user types) */}
                        {ccInput.trim() && (
                          <div
                            onClick={() => addCcEmail(ccInput)}
                            className="px-3.5 py-2.5 bg-purple-50/80 hover:bg-purple-100/90 border-b border-purple-100 cursor-pointer transition flex items-center justify-between group"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                                <FaEnvelope className="text-xs" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-slate-800 truncate">
                                  Add manual email: <span className="text-purple-700 font-mono font-bold">{ccInput.trim()}</span>
                                </p>
                                <p className="text-[10px] text-slate-400">
                                  Click here or press Enter to add this address
                                </p>
                              </div>
                            </div>
                            <span className="text-xs font-bold bg-purple-600 text-white px-2.5 py-1 rounded-lg shadow-2xs shrink-0 ml-2 group-hover:bg-purple-700 transition flex items-center gap-1">
                              + Add Email
                            </span>
                          </div>
                        )}

                        {/* Company Members Section */}
                        {filteredCompanyUsers.length > 0 ? (
                          <>
                            <div className="px-3.5 py-2 bg-slate-50 border-b border-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                              <span className="flex items-center gap-1.5">
                                <FaUsers className="text-purple-500 text-xs" />
                                <span>Company Members</span>
                              </span>
                              <span className="text-slate-400 font-medium">
                                {filteredCompanyUsers.length} available
                              </span>
                            </div>
                            <div className="divide-y divide-slate-100">
                              {filteredCompanyUsers.map((u) => {
                                const displayName = u.full_name || u.username || u.email;
                                const initials = (u.full_name || u.username || 'U')
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2);

                                return (
                                  <div
                                    key={u.id_user || u.email}
                                    onClick={() => addCcEmail(u.email)}
                                    className="px-3.5 py-2 hover:bg-purple-50/70 cursor-pointer transition flex items-center justify-between group"
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0 shadow-xs">
                                        {initials}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <p className="text-xs font-semibold text-slate-800 truncate group-hover:text-purple-700 transition">
                                            {displayName}
                                          </p>
                                          {u.role && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                                              {u.role}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[11px] text-slate-400 font-mono truncate">
                                          {u.email}
                                        </p>
                                      </div>
                                    </div>
                                    <span className="text-[11px] font-bold text-purple-600 opacity-0 group-hover:opacity-100 transition flex items-center gap-1 shrink-0 ml-2">
                                      + Add
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : !ccInput.trim() ? (
                          <div className="p-4 text-center text-xs text-slate-400">
                            No company members found. Type any email address to add manually.
                          </div>
                        ) : null}
                      </div>
                    )}

                    {ccError && <p className="text-xs text-red-500 mt-1">{ccError}</p>}

                    {/* CC Chips List */}
                    {ccList.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {ccList.map((email) => (
                          <span
                            key={email}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"
                          >
                            <span>{email}</span>
                            <button
                              type="button"
                              onClick={() => removeCcEmail(email)}
                              className="text-purple-400 hover:text-purple-700 transition"
                            >
                              <FaTimes className="text-[10px]" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SECTION 3: Detailed Description & Error Logs */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Describe in detail what happened, steps to reproduce the issue, and impact on operations..."
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    className="w-full p-3.5 rounded-xl border border-slate-200 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition placeholder:text-slate-400"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                    <span>Error / Logs (Optional)</span>
                    <span className="text-[11px] text-slate-400 font-normal">Stack trace or log snippet</span>
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g., HTTP 502 Bad Gateway / Error: CrashLoopBackOff: back-off 5m0s restarting failed container"
                    value={createForm.errorMessage}
                    onChange={(e) => setCreateForm({ ...createForm, errorMessage: e.target.value })}
                    className="w-full p-3 rounded-xl border border-slate-200 font-mono text-xs text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition placeholder:text-slate-400"
                  ></textarea>
                </div>
              </div>

              {/* SECTION 4: File Attachments with Multiple Support & Live Previews */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Attachments (Optional)
                  </label>
                  {createForm.attachments && createForm.attachments.length > 0 && (
                    <span className="text-xs text-blue-600 font-semibold">
                      {createForm.attachments.length} file{createForm.attachments.length > 1 ? 's' : ''} selected
                    </span>
                  )}
                </div>

                {/* Upload Dropzone with Drag and Drop Support */}
                <div
                  onDragEnter={handleTicketDragEnter}
                  onDragOver={handleTicketDragOver}
                  onDragLeave={handleTicketDragLeave}
                  onDrop={handleTicketDrop}
                  onClick={() => document.getElementById('ticket-attachments')?.click()}
                  className={`border-2 border-dashed rounded-2xl p-5 text-center transition-all duration-200 cursor-pointer ${
                    isDraggingTicketFiles
                      ? 'border-blue-500 bg-blue-50/90 scale-[1.01] ring-4 ring-blue-500/20 shadow-md'
                      : 'border-slate-200 hover:border-blue-400 bg-slate-50/50 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="file"
                    id="ticket-attachments"
                    multiple
                    onChange={handleFileChange}
                    onClick={(e) => e.stopPropagation()}
                    className="hidden"
                  />
                  <div className="cursor-pointer flex flex-col items-center justify-center space-y-1.5 select-none pointer-events-none">
                    <FiUploadCloud
                      className={`text-2xl transition-transform duration-200 ${
                        isDraggingTicketFiles ? 'text-blue-600 scale-125 animate-bounce' : 'text-blue-500'
                      }`}
                    />
                    <span className="text-xs font-semibold text-slate-700">
                      {isDraggingTicketFiles
                        ? 'Drop files here to upload'
                        : 'Click to select or drop files here (images, PDF, log, text)'}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      Supports multiple files • Max 5 MB per file
                    </span>
                  </div>
                </div>

                {/* Attachments Preview Grid */}
                {createForm.attachments && createForm.attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                      {createForm.attachments.map((file, idx) => {
                        const isImage = file.type.startsWith('image/');
                        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                        const isLog = file.name.toLowerCase().endsWith('.log') || file.name.toLowerCase().endsWith('.txt');
                        const previewUrl = isImage ? URL.createObjectURL(file) : null;

                        return (
                          <div
                            key={`${file.name}-${idx}`}
                            className="relative group bg-white border border-slate-200 rounded-xl p-2.5 flex items-center gap-2.5 shadow-xs hover:border-blue-300 transition"
                          >
                            {/* Thumbnail / Icon with preview click */}
                            <div
                              onClick={() => {
                                if (isImage) {
                                  setPreviewFile({
                                    url: previewUrl,
                                    name: file.name,
                                    type: file.type,
                                    size: file.size,
                                  });
                                }
                              }}
                              className={`w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden border border-slate-100 ${
                                isImage ? 'cursor-pointer hover:opacity-90 bg-slate-100' : 'bg-slate-50'
                              }`}
                              title={isImage ? 'Click to preview' : file.name}
                            >
                              {isImage ? (
                                <img
                                  src={previewUrl}
                                  alt={file.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : isPdf ? (
                                <FaFilePdf className="text-red-500 text-xl" />
                              ) : isLog ? (
                                <FaFileCode className="text-blue-500 text-xl" />
                              ) : (
                                <FaFileAlt className="text-slate-500 text-xl" />
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-800 truncate" title={file.name}>
                                {file.name}
                              </p>
                              <p className="text-[11px] text-slate-400">
                                {formatFileSize(file.size)}
                              </p>
                              {isImage && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPreviewFile({
                                      url: previewUrl,
                                      name: file.name,
                                      type: file.type,
                                      size: file.size,
                                    })
                                  }
                                  className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                                >
                                  <FaEye className="text-[10px]" />
                                  <span>Preview</span>
                                </button>
                              )}
                            </div>

                            {/* Remove button */}
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(idx)}
                              className="p-1 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                              title="Remove file"
                            >
                              <FaTimes className="text-xs" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-semibold text-sm transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md transition flex items-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin text-sm" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <AiOutlineSend className="text-base" />
                      <span>Submit Ticket</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUCCESS MODAL (Immediate feedback with Ticket ID)                         */}
      {/* ========================================================================= */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 text-center border border-slate-200/80 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <FaCheckCircle className="text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Ticket Submitted!</h3>
            <p className="text-slate-500 text-sm mt-1 mb-4">
              Your ticket has been created and email notifications have been sent.
            </p>

            {createdTicketResult && (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-left mb-6 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500">Ticket ID:</span>
                  <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                    {createdTicketResult.ticket_id}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500">Service:</span>
                  <span className="font-semibold text-slate-800">
                    {createdTicketResult.product_list}
                  </span>
                </div>
                {createdTicketResult.cc_emails && (
                  <div className="text-xs pt-1 border-t border-slate-200">
                    <span className="text-slate-500">CC:</span>
                    <span className="block text-purple-700 font-medium truncate mt-0.5">
                      {createdTicketResult.cc_emails}
                    </span>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => {
                setShowSuccessModal(false);
                setCreatedTicketResult(null);
              }}
              className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TICKET DETAILS MODAL (2-Column Standard Conversation & History Thread)   */}
      {/* ========================================================================= */}
      {showDetailModal && activeTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden border border-slate-200/80 my-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-slate-900 text-white p-6 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={(e) => handleCopyId(activeTicket.ticket_id, e)}
                    title="Click to copy Ticket ID"
                    className="inline-flex items-center gap-1.5 font-mono text-sm font-bold bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded-md border border-white/15 transition-colors cursor-pointer"
                  >
                    <span>{activeTicket.ticket_id}</span>
                    {copiedId === activeTicket.ticket_id ? (
                      <FaCheck className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <FaRegCopy className="w-3 h-3 text-slate-300 opacity-70" />
                    )}
                  </button>
                  {activeTicket.status === 'Open' ? (
                    <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-blue-500/20 text-blue-300 border border-blue-400/30">
                      Open
                    </span>
                  ) : activeTicket.status === 'On Progress' ? (
                    <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-amber-500/20 text-amber-300 border border-amber-400/30">
                      On Progress
                    </span>
                  ) : (
                    <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                      Closed
                    </span>
                  )}
                </div>
                <h2 className="text-lg font-bold mt-2 text-white">
                  {activeTicket.describe_issue}
                </h2>
              </div>
              <button
                onClick={handleCloseDetailModal}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
              >
                <FaTimes />
              </button>
            </div>

            {/* Content: 2 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 md:h-[680px] max-h-[82vh] overflow-hidden">
              {/* Left Column: Ticket Metadata */}
              <div className="p-6 space-y-4 overflow-y-auto h-full">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Ticket Details
                </h3>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 text-xs">Cloud Service:</span>
                    <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                      <FaCloud className="text-sky-500" />
                      {activeTicket.product_list}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 text-xs">Priority:</span>
                    <span className="font-semibold text-slate-800">
                      {activeTicket.priority}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 text-xs">Contact Email:</span>
                    <span className="font-medium text-slate-700 truncate max-w-[200px]">
                      {activeTicket.contact}
                    </span>
                  </div>

                  {/* CC Emails Display */}
                  <div className="py-1.5 border-b border-slate-100">
                    <div className="flex justify-between items-start">
                      <span className="text-slate-500 text-xs flex items-center gap-1">
                        <FaUsers className="text-purple-600 text-xs" />
                        <span>CC:</span>
                      </span>
                      <span className="text-xs font-medium text-slate-700 text-right">
                        {activeTicket.cc_emails || (
                          <span className="text-slate-400 italic">None</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {role === 'Admin' && activeTicket.company_name && (
                    <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                      <span className="text-slate-500 text-xs">Company:</span>
                      <span className="font-semibold text-slate-800">
                        {activeTicket.company_name}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                    <span className="text-slate-500 text-xs">Created:</span>
                    <span className="text-slate-700 text-xs font-medium">
                      {activeTicket.created_at
                        ? new Date(activeTicket.created_at).toLocaleString('en-US')
                        : '-'}
                    </span>
                  </div>

                  {/* Issue details */}
                  <div className="pt-2">
                    <span className="text-slate-500 text-xs block mb-1 font-semibold">
                      Description:
                    </span>
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                      {activeTicket.detail_issue || activeTicket.describe_issue}
                    </div>
                  </div>

                  {/* Attachments */}
                  {activeTicket.attachment && (
                    <div className="pt-2">
                      <span className="text-slate-500 text-xs block mb-1.5 font-semibold">
                        Attachments:
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {parseAttachmentUrls(activeTicket.attachment).map((url, i) => {
                            const fileName = url.split('/').pop().split('?')[0] || `Attachment ${i + 1}`;
                            const isImg = url.match(/\.(jpeg|jpg|png|gif|webp)($|\?)/i);
                            const isPdf = url.match(/\.pdf($|\?)/i);

                            return (
                              <div
                                key={i}
                                className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5 flex items-center justify-between gap-2.5 hover:border-blue-300 transition"
                              >
                                <div
                                  className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer"
                                  onClick={() => {
                                    if (isImg) {
                                      setPreviewFile({ url, name: fileName, isImage: true });
                                    } else {
                                      window.open(url, '_blank');
                                    }
                                  }}
                                >
                                  {isImg ? (
                                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0 bg-slate-100">
                                      <img
                                        src={url}
                                        alt={fileName}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ) : isPdf ? (
                                    <div className="w-10 h-10 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
                                      <FaFilePdf className="text-red-500 text-base" />
                                    </div>
                                  ) : (
                                    <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                                      <FaFileAlt className="text-blue-500 text-base" />
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-slate-800 truncate" title={fileName}>
                                      {fileName}
                                    </p>
                                    <span className="text-[11px] text-blue-600 hover:underline flex items-center gap-1">
                                      {isImg ? (
                                        <>
                                          <FaEye className="text-[10px]" />
                                          <span>Preview</span>
                                        </>
                                      ) : (
                                        <span>View file</span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  className="p-1.5 text-slate-400 hover:text-blue-600 transition"
                                  title="Download / Open"
                                >
                                  <FaExternalLinkAlt className="text-xs" />
                                </a>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Incident Report (CM Report if available) */}
                  {activeTicket.cm_report_url && (
                    <div className="pt-2">
                      <span className="text-slate-500 text-xs block mb-1 font-semibold">
                        Incident Report:
                      </span>
                      <a
                        href={activeTicket.cm_report_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200 transition"
                      >
                        <FaCheckCircle />
                        <span>Open Report</span>
                        <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Modern Chat Stream & Comments Thread */}
              <div className="flex flex-col h-full bg-slate-50/60 overflow-hidden">
                {/* Header */}
                <div className="p-5 pb-3.5 border-b border-slate-200/90 bg-white shrink-0 flex items-center justify-between shadow-2xs">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Activity & Comments
                    </h3>
                  </div>
                  <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {comments.length} comment{comments.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Chat message stream */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {loadingComments ? (
                    <div className="text-center py-16 text-slate-400 text-xs">
                      <FaSpinner className="animate-spin text-lg mx-auto mb-2 text-blue-500" />
                      Loading conversation...
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-16 text-slate-400 text-xs">
                      <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center mx-auto mb-2 text-lg shadow-2xs">
                        💬
                      </div>
                      <p className="font-semibold text-slate-600">No messages yet</p>
                      <p className="text-slate-400 text-[11px] mt-0.5">Send a message below to communicate with our support engineers.</p>
                    </div>
                  ) : (
                    comments.map((c, i) => {
                      const attUrls = parseAttachmentUrls(c.attachment);
                      const isMe = Boolean(
                        (userId && c.id_user === userId) ||
                        (user?.full_name && c.full_name && c.full_name.toLowerCase() === user.full_name.toLowerCase()) ||
                        (user?.email && c.id_user && c.id_user.toLowerCase() === user.email.toLowerCase())
                      );
                      const timeStr = c.timestamp
                        ? new Date(c.timestamp).toLocaleString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '';

                      return (
                        <div
                          key={i}
                          className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} gap-1`}
                        >
                          {/* Sender name & time */}
                          <div className={`flex items-center gap-1.5 text-[11px] text-slate-400 ${isMe ? 'mr-1' : 'ml-1'}`}>
                            <span className="font-semibold text-slate-600">
                              {isMe ? 'You' : (c.full_name || 'Support Team')}
                            </span>
                            <span>•</span>
                            <span>{timeStr}</span>
                          </div>

                          {/* Chat Bubble */}
                          <div
                            className={`max-w-[85%] rounded-2xl p-3.5 shadow-2xs text-xs leading-relaxed space-y-2.5 ${
                              isMe
                                ? 'bg-blue-600 text-white rounded-tr-xs shadow-blue-600/10'
                                : 'bg-white text-slate-800 border border-slate-200/90 rounded-tl-xs'
                            }`}
                          >
                            {c.comment && (
                              <p className="whitespace-pre-wrap leading-relaxed">
                                {c.comment}
                              </p>
                            )}

                            {/* Direct Inline Image & File Attachments */}
                            {attUrls.length > 0 && (
                              <div className="space-y-2 pt-1">
                                {attUrls.map((url, attIdx) => {
                                  const fileName = url.split('/').pop().split('?')[0];
                                  const ext = fileName.split('.').pop().toLowerCase();
                                  const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);

                                  if (isImg) {
                                    return (
                                      <div
                                        key={attIdx}
                                        onClick={() => setPreviewFile({ url, name: fileName, isImage: true })}
                                        className="group relative rounded-xl overflow-hidden border border-black/10 cursor-pointer bg-slate-900/5 max-w-sm shadow-xs"
                                      >
                                        <img
                                          src={url}
                                          alt={fileName}
                                          className="w-full max-h-60 object-cover hover:scale-[1.01] transition-transform duration-200"
                                        />
                                        <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                                          <span className="bg-slate-900/70 text-white text-[11px] font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 backdrop-blur-xs">
                                            <FaEye className="w-3 h-3" /> Click to enlarge
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      key={attIdx}
                                      className={`flex items-center justify-between gap-2 p-2 rounded-xl border text-xs ${
                                        isMe
                                          ? 'bg-blue-700/60 border-blue-400/40 text-white'
                                          : 'bg-slate-50 border-slate-200 text-slate-800'
                                      }`}
                                    >
                                      <div
                                        className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer"
                                        onClick={() => window.open(url, '_blank')}
                                      >
                                        {ext === 'pdf' ? (
                                          <FaFilePdf className={isMe ? 'text-red-200 text-base shrink-0' : 'text-red-500 text-base shrink-0'} />
                                        ) : (
                                          <FaFileAlt className={isMe ? 'text-blue-200 text-base shrink-0' : 'text-blue-500 text-base shrink-0'} />
                                        )}
                                        <span className="truncate font-medium" title={fileName}>
                                          {fileName}
                                        </span>
                                      </div>
                                      <a
                                        href={url}
                                        download={fileName}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`p-1 rounded transition shrink-0 ${
                                          isMe
                                            ? 'text-blue-200 hover:text-white hover:bg-blue-600'
                                            : 'text-slate-400 hover:text-blue-600 hover:bg-slate-100'
                                        }`}
                                        title="Download"
                                      >
                                        <FaExternalLinkAlt className="w-2.5 h-2.5" />
                                      </a>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={chatBottomRef} />
                </div>

                {/* Bottom Input Area Pinned to the Bottom */}
                <div
                  onDragEnter={handleCommentDragEnter}
                  onDragOver={handleCommentDragOver}
                  onDragLeave={handleCommentDragLeave}
                  onDrop={handleCommentDrop}
                  className={`bg-white border-t transition-all duration-200 ${
                    isDraggingCommentFiles
                      ? 'border-blue-500 bg-blue-50/70 ring-2 ring-blue-500/20'
                      : 'border-slate-200'
                  } shrink-0`}
                >
                  {isDraggingCommentFiles && (
                    <div className="p-2.5 bg-blue-100/90 border-b border-blue-200 text-blue-700 text-xs font-semibold flex items-center justify-center gap-2 pointer-events-none animate-pulse">
                      <FiUploadCloud className="text-base animate-bounce" />
                      <span>Drop file(s) here to attach to comment</span>
                    </div>
                  )}

                  {/* Selected Files Visual Image / Doc Preview Strip */}
                  {commentFiles.length > 0 && (
                    <div className="p-3 bg-slate-50/80 border-b border-slate-200 flex flex-wrap gap-2.5 max-h-36 overflow-y-auto">
                      {commentFiles.map((file, idx) => {
                        const isImg = file.type && file.type.startsWith('image/');
                        const previewUrl = isImg ? URL.createObjectURL(file) : null;

                        return (
                          <div
                            key={idx}
                            className="relative group shrink-0"
                          >
                            {isImg ? (
                              <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-blue-400 bg-slate-100 shadow-xs relative">
                                <img
                                  src={previewUrl}
                                  alt={file.name}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-x-0 bottom-0 bg-slate-900/60 text-[9px] text-white px-1 py-0.5 truncate text-center">
                                  {formatFileSize(file.size)}
                                </div>
                              </div>
                            ) : (
                              <div className="h-16 px-3 rounded-xl border border-blue-200 bg-blue-50/80 flex items-center gap-2 text-xs shadow-2xs">
                                <FaFileAlt className="text-blue-600 text-xl shrink-0" />
                                <div className="max-w-[110px]">
                                  <p className="truncate font-semibold text-[11px] text-slate-800" title={file.name}>
                                    {file.name}
                                  </p>
                                  <p className="text-[10px] text-slate-500">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveCommentFile(idx)}
                              className="absolute -top-1.5 -right-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md transition cursor-pointer"
                              title="Remove attachment"
                            >
                              <FaTimes className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Comment Input Box */}
                  <div className="p-3.5 flex items-center gap-2.5">
                    <input
                      type="file"
                      ref={commentFileInputRef}
                      onChange={handleCommentFileChange}
                      multiple
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.zip"
                    />
                    <button
                      type="button"
                      onClick={() => commentFileInputRef.current?.click()}
                      title="Attach file / image (Max 5 MB per file)"
                      className="w-10 h-10 rounded-xl border border-slate-200 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/50 text-slate-600 hover:text-blue-600 flex items-center justify-center transition shadow-2xs cursor-pointer shrink-0"
                    >
                      <FaPaperclip className="text-base" />
                    </button>
                    <textarea
                      rows={2}
                      placeholder="Write a message... (Press Shift+Enter for new line)"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onPaste={handleCommentPaste}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendComment();
                        }
                      }}
                      className="flex-1 p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition resize-none leading-relaxed"
                    ></textarea>
                    <button
                      onClick={handleSendComment}
                      disabled={isSubmittingComment || (!commentText.trim() && commentFiles.length === 0)}
                      className="w-10 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white flex items-center justify-center transition shadow-sm shrink-0 cursor-pointer disabled:cursor-not-allowed"
                    >
                      {isSubmittingComment ? (
                        <FaSpinner className="animate-spin text-base" />
                      ) : (
                        <AiOutlineSend className="text-lg" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* EDIT TICKET MODAL                                                         */}
      {/* ========================================================================= */}
      {isAdmin && showEditModal && activeTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 border border-slate-200/80 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">Edit Ticket</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleUpdateTicket} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Subject</label>
                <input
                  type="text"
                  required
                  value={editForm.describe_issue}
                  onChange={(e) => setEditForm({ ...editForm, describe_issue: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editForm.detail_issue}
                  onChange={(e) => setEditForm({ ...editForm, detail_issue: e.target.value })}
                  className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Priority</label>
                  <select
                    value={editForm.priority}
                    onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="P1 - Critical">P1 - Critical</option>
                    <option value="P2 - High impact">P2 - High impact</option>
                    <option value="P3 - Medium impact">P3 - Medium impact</option>
                    <option value="P4 - Low impact">P4 - Low impact</option>
                  </select>
                </div>

                {/* Status: Available to Admin and users */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-semibold"
                  >
                    <option value="Open">Open</option>
                    <option value="On Progress">On Progress</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>

              {/* CC Emails Edit Field */}
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">
                  CC Email(s) (comma-separated)
                </label>
                <input
                  type="text"
                  placeholder="user1@company.com, user2@company.com"
                  value={editForm.cc_emails}
                  onChange={(e) => setEditForm({ ...editForm, cc_emails: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow transition"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION MODAL                                                 */}
      {/* ========================================================================= */}
      {isAdmin && showDeleteModal && activeTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 text-center border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-14 h-14 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
              <FaTrash className="text-xl" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">Delete Ticket?</h3>
            <p className="text-slate-500 text-xs mt-1 mb-5">
              Are you sure you want to delete ticket{' '}
              <span className="font-mono font-bold text-slate-800">{activeTicket.ticket_id}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTicket}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold shadow flex-1"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ATTACHMENT PREVIEW MODAL / LIGHTBOX */}
      {previewFile && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-4xl max-h-[90vh] w-full overflow-hidden shadow-2xl flex flex-col border border-slate-200 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2.5 truncate mr-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <FaPaperclip className="text-sm" />
                </div>
                <div className="truncate">
                  <span className="text-sm font-bold text-slate-800 block truncate">
                    {previewFile.name}
                  </span>
                  {previewFile.size && (
                    <span className="text-[11px] text-slate-400">
                      {formatFileSize(previewFile.size)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={previewFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={previewFile.name}
                  className="px-3 py-1.5 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-semibold flex items-center gap-1.5 transition"
                >
                  <FaExternalLinkAlt className="text-[10px]" />
                  <span>Open Full</span>
                </a>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200 flex items-center justify-center transition"
                  title="Close preview"
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
            </div>

            {/* Content Preview */}
            <div className="p-4 flex-1 overflow-auto flex items-center justify-center bg-slate-900/5 min-h-[300px]">
              <img
                src={previewFile.url}
                alt={previewFile.name}
                className="max-h-[75vh] max-w-full object-contain rounded-xl shadow-md border border-slate-200"
              />
            </div>
          </div>
        </div>
      )}

      {/* INFORMATIVE ERROR MODAL POPUP */}
      {errorModal.isOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => setErrorModal({ ...errorModal, isOpen: false })}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-red-100 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4 border border-red-100">
              <FiAlertTriangle className="text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 text-center">
              {errorModal.title || 'Error'}
            </h3>
            <p className="text-slate-600 text-xs mt-2 text-center leading-relaxed">
              {errorModal.message}
            </p>
            {errorModal.status && (
              <div className="mt-3 text-center">
                <span className="inline-block text-[10px] font-mono font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded border border-slate-200">
                  Status Code: {errorModal.status}
                </span>
              </div>
            )}
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setErrorModal({ ...errorModal, isOpen: false })}
                className="w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition shadow-sm"
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportRequest;